import type { FC } from "react";
import { useEffect } from "react";

import { Card, CardRowSimple, type GridCard, LoadingSpinner } from "widgets";

import { useError } from "@/app-utils/graphql/useError.ts";
import type { PlaybookEvent } from "@/graphql.ts";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";
import { PlaybookEventTile } from "@/ui-modules/core/tiles";
import { useGetPlaybookEventStatusCardLazyQuery } from "@/ui-modules/playbooks/events/graphql/event-queries.gql.ts";

export interface EventStatusCardProps extends GridCard {
  organismId: number;
}

const EventStatusCard: FC<EventStatusCardProps> = ({ organismId }) => {
  const [fetchEvents, { data, loading, error }] = useGetPlaybookEventStatusCardLazyQuery({
    variables: { organismId: organismId },
  });
  void useError(error);

  /* =========== Validation =========== */
  useEffect(() => {
    // we get an organismId of 0 in case of contig summary pages,
    // i.e. when the genome is hidden for the current user. In that
    // case fetching with an ID of 0 will lead to a validation exception.
    if (organismId && organismId > 0) {
      void fetchEvents();
    }
  }, []);

  // no need to mount the card when the genome is hidden on a contig summary page.
  // On Genome summary pages it will be mount and displays a placeholder when there
  // are no related events
  if (!organismId || organismId <= 0) {
    return null;
  }
  /* =========== Validation =========== */

  const playbookEvents = data?.organism?.playbookEvents;

  return (
    <Card title={"Related Playbook Events"} shrinkable={true}>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {!playbookEvents?.length ? (
            <ContentPlaceholder>No related Playbook Events</ContentPlaceholder>
          ) : (
            <CardRowSimple>
              {playbookEvents?.map((playbookEvent: PlaybookEvent, index) => (
                <PlaybookEventTile event={playbookEvent} key={`event-status-card-tile-${index}`} />
              ))}
            </CardRowSimple>
          )}
        </>
      )}
    </Card>
  );
};

export { EventStatusCard };
